import React, { useEffect, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  PublicStateAgencyMapping,
  stateAgencyMappingService,
} from '../services/stateAgencyMappingService';

// FEAT-047 — Caregiver-facing "Register with [agency]" link (web).
// MVP-8.16 (register entry point), MVP-8.17 (number on file hides link),
// MVP-8.35b (visible in sign-up + settings until number entered),
// MVP-8.35c (unmapped state renders nothing).
//
// Tone: warm and elderly-friendly per §1.2 — "Register with HCSB" rather
// than "Required: register state license". The component renders nothing
// (no broken link, no generic copy) when the state is unmapped OR when a
// license number is already on file.

export interface StateAgencyRegisterLinkProps {
  stateCode: string | null | undefined;
  licenseNumber: string | null | undefined;
  surface?: 'signup' | 'settings';
}

export const StateAgencyRegisterLink: React.FC<StateAgencyRegisterLinkProps> = ({
  stateCode,
  licenseNumber,
  surface = 'settings',
}) => {
  const [mapping, setMapping] = useState<PublicStateAgencyMapping | null>(null);
  const [loaded, setLoaded] = useState(false);

  const trimmedLicense = licenseNumber?.trim() ?? '';
  const hasLicense = trimmedLicense.length > 0;

  useEffect(() => {
    let cancelled = false;
    if (!stateCode || hasLicense) {
      setMapping(null);
      setLoaded(true);
      return () => {
        cancelled = true;
      };
    }
    setLoaded(false);
    stateAgencyMappingService
      .getByStateCode(stateCode)
      .then((result) => {
        if (!cancelled) {
          setMapping(result);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMapping(null);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stateCode, hasLicense]);

  if (!loaded) return null;
  if (hasLicense || !mapping) return null;

  return (
    <Box
      sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}
      data-testid="state-agency-register-link"
      data-surface={surface}
    >
      <Link
        href={mapping.registerUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontWeight: 500 }}
        aria-label={`Register with ${mapping.agencyLabel} (opens in a new tab)`}
      >
        Register with {mapping.agencyLabel}
        <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
      </Link>
      <Typography variant="caption" color="text.secondary">
        It only takes a few minutes.
      </Typography>
    </Box>
  );
};

export default StateAgencyRegisterLink;
